import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import Config from 'src/common/Config';
import { useAppContainerContext } from '../AppContainerContext';
import { SiestaAction } from '../types';
import { Logger } from 'src/utils/Logger';

const API_URL = Config.getApiUrl();

type SiestaActionsConfig = {
  [pageType: string]: SiestaAction[];
};

const SIESTA_ACTIONS_CONFIG: SiestaActionsConfig = {
  'suitability-report-edit': [
    { id: 'shorten', label: 'Make the document shorter' },
    { id: 'simplify', label: 'Simplify technical language and improve readability' },
    { id: 'summary', label: 'Add a concise executive summary at the top' },
  ],
  default: [
    { id: 'summarize', label: 'Summarize key information' },
    { id: 'improve', label: 'Suggest improvements' },
    { id: 'check', label: 'Check for errors' },
  ],
};

type UseSuggestedActionsParams = {
  documentId?: string;
  documentContent?: string;
  clientId?: string;
  conversationId?: string;
};

type GenerateSuggestionsResponse = {
  suggestions: string[];
};

export function useSuggestedActions(params?: UseSuggestedActionsParams): {
  actions: SiestaAction[];
  isLoading: boolean;
} {
  const { contentMeta } = useAppContainerContext();
  const pageType = contentMeta?.pageType;

  const { documentId, documentContent, clientId, conversationId } = params ?? {};
  const canFetch = !!(documentId && documentContent && clientId && conversationId);

  const { data, isLoading } = useQuery<GenerateSuggestionsResponse>({
    queryKey: ['suggested-actions', documentId],
    queryFn: async () => {
      const response = await fetch(
        `${API_URL}/documents/${documentId}/suggested-actions/generate`,
        {
          method: 'POST',
          body: JSON.stringify({ documentContent, clientId, conversationId }),
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        Logger.error(
          'Failed to generate suggested actions',
          new Error('Failed to generate suggested actions'),
          { documentId, response },
        );
        return { suggestions: [] };
      }

      return response.json();
    },
    enabled: canFetch,
    staleTime: Infinity,
  });

  return useMemo(() => {
    if (data?.suggestions?.length) {
      return {
        actions: data.suggestions.map((text: string, i: number) => ({
          id: `ai-${i}`,
          label: text,
        })),
        isLoading: false,
      };
    }

    const fallback =
      pageType && SIESTA_ACTIONS_CONFIG[pageType]
        ? SIESTA_ACTIONS_CONFIG[pageType]
        : SIESTA_ACTIONS_CONFIG.default;

    return { actions: fallback, isLoading };
  }, [data, pageType, isLoading]);
}
